import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { useDispatch, useSelector } from 'react-redux';
import { updateBandsintownSettings, updateLocalSettings } from '../../store/bandsintown/bandsintownSlice';
import { setErrorAlert, setSuccessAlert } from '../../store/alert/alertSlice';
import { ReactComponent as BandsintownLogo } from '../../assets/images/components/bandsintown-svgrepo-com.svg';

const BandsintownConnector = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = JSON.parse(localStorage.getItem('dizeeToken'));
  const bandsintownSettings = useSelector(state => state.bandsintown.settings);
  
  const [apiKey, setApiKey] = useState('');
  const [artistName, setArtistName] = useState('');
  const [autoSync, setAutoSync] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  useEffect(() => {
    if (bandsintownSettings) {
      setApiKey(bandsintownSettings.apiKey || '');
      setArtistName(bandsintownSettings.artistName || '');
      setAutoSync(bandsintownSettings.autoSync !== false);
      setIsConnected(!!(bandsintownSettings.apiKey && bandsintownSettings.artistName));
    }
  }, [bandsintownSettings]);

  const testConnection = async () => {
    if (!apiKey || !artistName) {
      dispatch(setErrorAlert('Please fill in both API key and artist name'));
      return;
    }

    setIsTesting(true);
    try {
      const response = await fetch(
        `https://rest.bandsintown.com/artists/${encodeURIComponent(artistName.trim())}/events?app_id=${apiKey.trim()}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to connect to Bandsintown');
      }

      const data = await response.json();
      
      if (Array.isArray(data)) {
        dispatch(setSuccessAlert(`Connection successful! Found ${data.length} events for ${artistName}`));
        setIsConnected(true);
      } else {
        throw new Error('Invalid response from Bandsintown API');
      }
    } catch (error) {
      console.error('Bandsintown connection test failed:', error);
      dispatch(setErrorAlert('Connection failed. Please check your API key and artist name.'));
      setIsConnected(false);
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    if (!apiKey || !artistName) {
      dispatch(setErrorAlert('Please fill in both API key and artist name'));
      return;
    }

    try {
      // Save settings locally for now (until backend endpoint is implemented)
      const settings = {
        artistName: artistName.trim(),
        apiKey: apiKey.trim(),
        autoSync,
        lastUpdated: new Date().toISOString()
      };
      
      // Save to localStorage
      localStorage.setItem('bandsintownSettings', JSON.stringify(settings));
      
      // Update Redux store locally
      dispatch(updateLocalSettings(settings));
      
      dispatch(setSuccessAlert('Bandsintown settings saved successfully!'));
      setIsConnected(true);
    } catch (error) {
      console.error('Failed to save Bandsintown settings:', error);
      dispatch(setErrorAlert('Failed to save settings. Please try again.'));
    }
  };

  const handleImportShows = () => {
    if (!isConnected) {
      dispatch(setErrorAlert('Please save your settings first'));
      return;
    }
    
    // Navigate to bulk import with Bandsintown pre-selected
    navigate('/bookings/bulk-import', { 
      state: { selectedFormat: 'bandsintown' } 
    });
  };

  const handleDisconnect = () => {
    setApiKey('');
    setArtistName('');
    setAutoSync(true);
    setIsConnected(false);
    
    // Clear settings from localStorage and Redux store
    localStorage.removeItem('bandsintownSettings');
    dispatch(updateLocalSettings(null));
    
    dispatch(setSuccessAlert('Bandsintown account disconnected'));
  };

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <div className="flex items-center gap-2">
              <BandsintownLogo className="w-[20px] h-[20px]" />
              <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Bandsintown</span>
            </div>
            <span 
              className="text-white text-[12px] cursor-pointer hover:opacity-70" 
              onClick={() => navigate(-1)}
              style={{ fontWeight: 300 }}
            >
              Go back
            </span>
          </div>

          <div className="w-full px-4 pb-20 overflow-y-auto flex-1">
            {/* Connection Status */}
            <div className="mb-6 p-4 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white text-[12px]" style={{ fontWeight: 300 }}>Connection Status</span>
                {isConnected ? (
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-green-500 text-[10px]">Connected</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-red-500 rounded-full" />
                    <span className="text-red-500 text-[10px]">Not Connected</span>
                  </div>
                )}
              </div>
              <p className="text-[rgba(255,255,255,0.7)] text-[10px]">
                {isConnected 
                  ? 'Your Bandsintown account is connected and ready to sync shows.'
                  : 'Connect your Bandsintown account to import and sync your tour dates.'
                }
              </p>
            </div>

            {/* Setup Form */}
            <div className="space-y-4 mb-6">
              {/* Artist Name */}
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#4286f5] hover:bg-[rgba(66,134,245,0.05)] transition-all">
                <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                  Artist Name
                </label>
                <input
                  type="text"
                  value={artistName}
                  onChange={(e) => setArtistName(e.target.value)}
                  className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                  placeholder="Your artist name on Bandsintown"
                />
              </div>

              {/* API Key */}
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#4286f5] hover:bg-[rgba(66,134,245,0.05)] transition-all">
                <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                  API Key
                </label>
                <input
                  type="text"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                  placeholder="Your Bandsintown API key"
                />
              </div>
                
              <div className="p-3 bg-[rgba(255,255,0,0.1)] border border-[rgba(255,255,0,0.3)] rounded-lg">
                <p className="text-[#FFD700] text-[10px] leading-relaxed" style={{ fontWeight: 300 }}>
                  <span style={{ fontWeight: 400 }}>How to get your API key:</span><br/>
                  1. Visit bandsintown.com and log into your artist account<br/>
                  2. Go to your artist dashboard<br/>
                  3. Navigate to API settings<br/>
                  4. Copy your API key
                </p>
              </div>

              {/* Auto-sync option */}
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <input
                    type="checkbox"
                    id="autoSync"
                    checked={autoSync}
                    onChange={(e) => setAutoSync(e.target.checked)}
                    className="w-4 h-4 accent-[#4286f5]"
                  />
                  <label htmlFor="autoSync" className="text-white text-[12px]" style={{ fontWeight: 300 }}>
                    Auto-sync every 24 hours
                  </label>
                </div>
                <p className="text-[rgba(255,255,255,0.7)] text-[10px]" style={{ fontWeight: 300 }}>
                  Automatically check for new shows and update your bookings daily
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Connect - Only show when not connected */}
              {!isConnected && (
                <button
                  onClick={testConnection}
                  disabled={!apiKey || !artistName || isTesting}
                  className="w-full bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] text-white py-3 rounded-lg hover:bg-[rgba(255,255,255,0.15)] disabled:opacity-50 disabled:cursor-not-allowed transition-all text-[12px]"
                >
                  {isTesting ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Testing Connection...
                    </div>
                  ) : (
                    'Connect'
                  )}
                </button>
              )}

              {/* Save Settings */}
              <button
                onClick={handleSave}
                disabled={!apiKey || !artistName}
                className="w-full bg-[#4286f5] text-white py-3 rounded-lg hover:bg-[#366bc7] disabled:opacity-50 disabled:cursor-not-allowed transition-all text-[12px] font-medium"
              >
                Save Settings
              </button>

              {/* Import Shows */}
              {isConnected && (
                <button
                  onClick={handleImportShows}
                  className="w-full bg-[#4286f5] text-white py-3 rounded-lg hover:bg-[#366bc7] transition-all text-[12px] font-medium"
                >
                  Import Shows to Bookings
                </button>
              )}

              {/* Disconnect */}
              {isConnected && (
                <button
                  onClick={handleDisconnect}
                  className="w-full bg-red-500 text-black py-3 rounded-lg hover:bg-red-600 transition-all text-[12px] font-medium"
                >
                  Disconnect Account
                </button>
              )}
            </div>

          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default BandsintownConnector;
